import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle2, ListChecks, Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { isApiError } from '@/lib/api/errors'
import { useModuleContent } from '../../catalogApi'
import type { CatalogModuleView, ModuleFeature } from '../../types'
import { toastError } from '../../ui'
import { RowMenu } from '../RowMenu'

/** Modülün kapsadığı işlemler; gruplara ayrılmış liste. */
export function ModuleFeatures({ module: m, canWrite }: { module: CatalogModuleView; canWrite: boolean }) {
  const { t } = useTranslation()
  const { deleteFeature } = useModuleContent(m.id)
  const [dialog, setDialog] = useState<{ open: boolean; feature: ModuleFeature | null; group?: string }>({ open: false, feature: null })
  const groups = [...new Set(m.features.map((f) => f.group))]

  const onDelete = async (f: ModuleFeature) => {
    if (!(await confirmModal({ title: t('projects.catalog.features.deleteTitle'), description: t('projects.catalog.features.deleteBody', { name: f.name, module: m.name }), confirmLabel: t('common.delete'), destructive: true }))) return
    deleteFeature.mutate(f.id, { onSuccess: () => toast.success(t('projects.catalog.features.deleted')), onError: toastError })
  }

  return (
    <section className="rounded-xl border bg-card shadow-card">
      <header className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <ListChecks className="size-4 text-muted-foreground" aria-hidden />
        <h2 className="font-semibold">{t('projects.catalog.features.title')}</h2>
        <Badge tone="info">{t('projects.catalog.features.count', { count: m.features.length })}</Badge>
        {canWrite ? (
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setDialog({ open: true, feature: null })}>
            <Plus />
            {t('projects.catalog.features.add')}
          </Button>
        ) : null}
      </header>
      {m.features.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('projects.catalog.features.empty')}</p>
      ) : (
        <div className="@container divide-y">
          {groups.map((g) => (
            <div key={g} className="space-y-2 p-4">
              <div className="flex items-center gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g}</h3>
                {canWrite ? (
                  <Button size="icon" variant="ghost" className="size-6" onClick={() => setDialog({ open: true, feature: null, group: g })} aria-label={t('projects.catalog.features.addTo', { group: g })}>
                    <Plus className="size-3.5" />
                  </Button>
                ) : null}
              </div>
              <ul className="grid grid-cols-1 gap-2 @2xl:grid-cols-2">
                {m.features
                  .filter((f) => f.group === g)
                  .map((f) => (
                    <li key={f.id} className="flex items-start gap-2.5 rounded-lg border bg-muted/20 p-3">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{f.name}</p>
                        {f.description ? <p className="text-xs leading-relaxed text-muted-foreground">{f.description}</p> : null}
                      </div>
                      {canWrite ? <RowMenu label={f.name} onEdit={() => setDialog({ open: true, feature: f })} onDelete={() => void onDelete(f)} /> : null}
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {dialog.open ? (
        <FeatureForm key={dialog.feature?.id ?? `new-${dialog.group ?? ''}`} module={m} feature={dialog.feature} group={dialog.group} onDone={() => setDialog({ open: false, feature: null })} />
      ) : null}
    </section>
  )
}

type FeatureFormProps = {
  module: CatalogModuleView | null
  catalog?: CatalogModuleView[]
  feature: ModuleFeature | null
  group?: string
  onDone: () => void
}

export function FeatureForm({ module: fixed, catalog = [], feature, group, onDone }: FeatureFormProps) {
  const { t } = useTranslation()
  const [moduleId, setModuleId] = useState(fixed?.id ?? '')
  const m = fixed ?? catalog.find((x) => x.id === moduleId) ?? null
  const groups = [...new Set((m?.features ?? []).map((f) => f.group))]
  const { saveFeature } = useModuleContent(moduleId)
  const [moduleError, setModuleError] = useState('')
  const schema = z.object({
    group: z.string().trim().min(2, t('projects.catalog.features.groupRequired')).max(40),
    name: z.string().trim().min(2, t('projects.catalog.features.nameRequired')).max(80),
    description: z.string().trim().max(240),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { group: feature?.group ?? group ?? groups[0] ?? '', name: feature?.name ?? '', description: feature?.description ?? '' } })
  const { errors } = form.formState

  const onSubmit = form.handleSubmit(async (input) => {
    if (!m) {
      setModuleError(t('projects.catalog.integrate.moduleRequired'))
      return
    }
    try {
      await saveFeature.mutateAsync({ id: feature?.id, input })
      toast.success(t(feature ? 'projects.catalog.features.updated' : 'projects.catalog.features.created'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'FEATURE_NAME_TAKEN') form.setError('name', { message: t('errors.FEATURE_NAME_TAKEN') })
      else toastError(e)
    }
  })

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!saveFeature.isPending}
      icon={<ListChecks />}
      title={t(feature ? 'projects.catalog.features.editTitle' : 'projects.catalog.features.createTitle')}
      description={m ? t('projects.catalog.features.hint', { module: m.name }) : t('projects.catalog.featureList.formHint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={saveFeature.isPending}>
            {saveFeature.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!fixed ? (
          <div className="space-y-2">
            <Label htmlFor="ft-module">{t('projects.catalog.featureList.module')}</Label>
            <Select
              id="ft-module"
              value={moduleId}
              disabled={!!feature}
              invalid={!!moduleError}
              placeholder={t('projects.catalog.featureList.chooseModule')}
              onChange={(v) => {
                setModuleId(v)
                setModuleError('')
                const first = catalog.find((x) => x.id === v)?.features[0]?.group
                if (first && !form.getValues('group')) form.setValue('group', first)
              }}
              options={catalog.map((x) => ({ value: x.id, label: x.name, description: t('projects.catalog.features.count', { count: x.features.length }) }))}
            />
            {moduleError ? <p className="text-xs text-destructive">{moduleError}</p> : null}
          </div>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="ft-group">{t('projects.catalog.features.group')}</Label>
          <Input id="ft-group" list="ft-groups" aria-invalid={!!errors.group} aria-describedby="ft-group-hint" {...form.register('group')} />
          <datalist id="ft-groups">
            {groups.map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
          {errors.group ? <p className="text-xs text-destructive">{errors.group.message}</p> : <p id="ft-group-hint" className="text-xs text-muted-foreground">{t('projects.catalog.features.groupHint')}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="ft-name">{t('projects.catalog.features.name')}</Label>
          <Input id="ft-name" aria-invalid={!!errors.name} {...form.register('name')} />
          {errors.name ? <p className="text-xs text-destructive">{errors.name.message}</p> : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="ft-desc">{t('projects.fields.description')}</Label>
          <Textarea id="ft-desc" rows={3} maxLength={240} {...form.register('description')} />
        </div>
      </div>
    </Modal>
  )
}
